import * as React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
} from '@mui/material';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import GroupsIcon from '@mui/icons-material/Groups';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ArticleIcon from '@mui/icons-material/Article';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { hasPermission } from '../../lib/authz';
import type { RootState } from '../../store/types/sharedTypes';
import { DS } from '../PatientForms/designTokens';
import { GENERAL_FONT } from '../../constants/theme';
import { SideMenuItems } from '../../lib/interfaces';

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navItemSx = {
    marginTop: '12px',
    // inset so the pill floats in the drawer: flush to the edges its radius is
    // clipped, and on the right it fuses with the white content area
    marginLeft: '8px',
    marginRight: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    borderRadius: '16px',
    transition: 'background-color 120ms cubic-bezier(0.22, 0.61, 0.36, 1)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: DS.surfaceRaised,
      boxShadow: DS.shadowSm,
    },
  };
  const listItemTextStyle = (selected: boolean) => ({
    fontWeight: 700,
    color: selected ? DS.deepTeal : DS.inkSecondary,
    fontFamily: GENERAL_FONT,
  });
  const listItemIconStyle = (selected: boolean) => ({
    color: selected ? DS.deepTeal : DS.inkSecondary,
  });
  const { sessionUser } = useSelector((state: RootState) => state.general);
  const canWriteTemplates = hasPermission(
    sessionUser,
    'template:writeTemplate'
  );
  const isTechnicalAdmin = hasPermission(sessionUser, 'technicalAdmin');
  const canExportData = hasPermission(sessionUser, 'data:export');

  const isSelected = (item: string) => {
    if (
      location?.pathname === '/dashboard' &&
      item === SideMenuItems.Dashboard
    ) {
      return true;
    }
    if (
      location.pathname.includes('admins') &&
      item === SideMenuItems.AdminUsers
    ) {
      return true;
    }
    if (
      location.pathname.includes('patients') &&
      item === SideMenuItems.AllPatients
    ) {
      return true;
    }
    if (
      location.pathname.includes('submissions') &&
      item === SideMenuItems.UnlockRequests
    ) {
      return true;
    }
    if (
      location.pathname.includes('templates') &&
      item === SideMenuItems.Templates
    ) {
      return true;
    }
    if (
      location.pathname.includes('visit-runs') &&
      item === SideMenuItems.VisitRuns
    ) {
      return true;
    }
    if (
      location.pathname.includes('app-version') &&
      item === SideMenuItems.AppVersion
    ) {
      return true;
    }
    if (
      location.pathname.includes('forms-export') &&
      item === SideMenuItems.FormsExport
    ) {
      return true;
    }
    return false;
  };

  const navigateToPath = (path: string) => {
    navigate(path);
  };

  return (
    <List>
      <ListItem>
        <Box
          component="img"
          src="/image/mh_logo.svg"
          alt="Migränhjälpen"
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: 200,
            height: 'auto',
            // the old png carried 50px of transparent padding above its ink and
            // 65px below; the svg is cropped tight, so that breathing room is
            // restored here to keep the original rhythm
            marginTop: '44px',
            marginBottom: '65px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
      </ListItem>
      <ListItemButton
        sx={navItemSx}
        disableRipple
        selected={isSelected(SideMenuItems.AllPatients)}
        onClick={() => navigateToPath('/dashboard/patients')}
      >
        <ListItemIcon>
          <GroupsIcon
            style={listItemIconStyle(isSelected(SideMenuItems.AllPatients))}
          />
        </ListItemIcon>
        <Typography
          style={listItemTextStyle(isSelected(SideMenuItems.AllPatients))}
        >
          {' '}
          {t('allPatients')}{' '}
        </Typography>
      </ListItemButton>
      <ListItemButton
        sx={navItemSx}
        disableRipple
        selected={isSelected(SideMenuItems.AdminUsers)}
        onClick={() => navigateToPath('/dashboard/admins')}
      >
        <ListItemIcon>
          <HeadsetMicIcon
            style={listItemIconStyle(isSelected(SideMenuItems.AdminUsers))}
          />
        </ListItemIcon>
        <Typography
          style={listItemTextStyle(isSelected(SideMenuItems.AdminUsers))}
        >
          {' '}
          {t('adminUsers')}{' '}
        </Typography>
      </ListItemButton>

      {canWriteTemplates && (
        <ListItemButton
          sx={navItemSx}
          disableRipple
          selected={isSelected(SideMenuItems.Templates)}
          onClick={() => navigateToPath('/dashboard/templates')}
        >
          <ListItemIcon>
            <ArticleIcon
              style={listItemIconStyle(isSelected(SideMenuItems.Templates))}
            />
          </ListItemIcon>
          <Typography
            style={listItemTextStyle(isSelected(SideMenuItems.Templates))}
          >
            {t('template.templateName')}
          </Typography>
        </ListItemButton>
      )}

      {isTechnicalAdmin && (
        <>
          <ListItem style={{ marginTop: '24px', paddingBottom: 0 }}>
            <Typography
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: DS.inkSecondary,
                fontFamily: GENERAL_FONT,
              }}
            >
              {t('technicalAdminSection')}
            </Typography>
          </ListItem>

          <ListItemButton
            sx={navItemSx}
            disableRipple
            selected={isSelected(SideMenuItems.VisitRuns)}
            onClick={() => navigateToPath('/dashboard/visit-runs')}
          >
            <ListItemIcon>
              <EventRepeatIcon
                style={listItemIconStyle(isSelected(SideMenuItems.VisitRuns))}
              />
            </ListItemIcon>
            <Typography
              style={listItemTextStyle(isSelected(SideMenuItems.VisitRuns))}
            >
              {t('visitRun.menu')}
            </Typography>
          </ListItemButton>

          <ListItemButton
            sx={navItemSx}
            disableRipple
            selected={isSelected(SideMenuItems.AppVersion)}
            onClick={() => navigateToPath('/dashboard/app-version')}
          >
            <ListItemIcon>
              <PhoneIphoneIcon
                style={listItemIconStyle(isSelected(SideMenuItems.AppVersion))}
              />
            </ListItemIcon>
            <Typography
              style={listItemTextStyle(isSelected(SideMenuItems.AppVersion))}
            >
              {t('appVersion.menu')}
            </Typography>
          </ListItemButton>
        </>
      )}

      {canExportData && (
        <ListItemButton
          sx={navItemSx}
          disableRipple
          selected={isSelected(SideMenuItems.FormsExport)}
          onClick={() => navigateToPath('/dashboard/forms-export')}
        >
          <ListItemIcon>
            <CloudUploadIcon
              style={listItemIconStyle(isSelected(SideMenuItems.FormsExport))}
            />
          </ListItemIcon>
          <Typography
            style={listItemTextStyle(isSelected(SideMenuItems.FormsExport))}
          >
            {t('formsExport.menu')}
          </Typography>
        </ListItemButton>
      )}
    </List>
  );
};

export default SideMenu;
